import React from 'react';

import _ from 'lodash';
import { Clear, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import {
  Autocomplete,
  autocompleteClasses,
  Divider,
  IconButton,
  Popper,
  Stack,
  styled,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';

import { Search, SearchIconWrapper, StyledInputBase } from './Search';

export interface SearchTextFieldIndex {
  currentIndex: number | undefined;
  totalAmount: number;
}

export interface SearchSuggestion {
  label: string;
  searchTerm: string;
}

type Props = {
  searchId: string;
  inputHtmlId?: string;
  delegate: SearchTextFieldDelegate;

  icon?: React.ReactElement;
  placeholder?: string;
  style?: React.CSSProperties;

  term: string;
  caseSensitive: boolean;
  regex: boolean;
  index?: SearchTextFieldIndex;

  suggestions?: SearchSuggestion[];
};

export interface SearchTextFieldDelegate {
  search(searchId: string, term: string): Promise<void>;
  jumpToSearchTerm(searchId: string, termIndex: number): void;
  clearSearch(searchId: string): void;

  toggleRegex(searchId: string): void;
  toggleCaseSensitivity(searchId: string): void;
}

export class SearchTextField extends React.Component<Props> {
  private debouncedTerm?: string;
  private searchContainerRef = React.createRef<HTMLDivElement>();

  private debouncedSearchCall = _.debounce((term: string) => {
    this.debouncedTerm = undefined;
    this.props.delegate.search(this.props.searchId, term).catch(console.error);
  }, 300);

  render() {
    const currentValue =
      typeof this.debouncedTerm === 'undefined' ? this.props.term : this.debouncedTerm;
    const hasSuggestions = this.props.suggestions && this.props.suggestions.length > 0;

    return (
      <Search style={this.props.style} ref={this.searchContainerRef}>
        <Stack direction="row" alignItems="center" sx={{ marginRight: '8px' }}>
          {this.props.icon ? <SearchIconWrapper>{this.props.icon}</SearchIconWrapper> : null}

          {hasSuggestions ? (
            <Autocomplete
              freeSolo
              fullWidth
              disableClearable
              options={this.props.suggestions || []}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
              filterOptions={(options) => options} // Always show all suggestions, don't filter
              inputValue={currentValue}
              onInputChange={(_event, newValue, reason) => {
                if (reason === 'input' || reason === 'clear') {
                  this.debouncedTerm = newValue;
                  this.debouncedSearchCall(newValue);
                  this.forceUpdate();
                }
              }}
              onChange={(_event, newValue) => {
                if (newValue && typeof newValue !== 'string') {
                  this.handleSuggestionClick(newValue);
                }
              }}
              ListboxProps={{
                style: { maxHeight: '300px' },
              }}
              PopperComponent={(props) => {
                const searchContainer = this.searchContainerRef.current;
                return (
                  <StyledPopper
                    {...props}
                    anchorEl={searchContainer}
                    modifiers={[
                      {
                        name: 'offset',
                        options: {
                          offset: [0, 4],
                        },
                      },
                    ]}
                    style={{
                      ...props.style,
                      width: searchContainer?.offsetWidth ?? 'auto',
                    }}
                  />
                );
              }}
              sx={{
                flex: 1,
                '& .MuiAutocomplete-inputRoot': {
                  padding: 0,
                },
              }}
              renderInput={(params) => (
                <StyledInputBase
                  ref={params.InputProps.ref}
                  id={this.props.inputHtmlId}
                  inputProps={{
                    ...params.inputProps,
                    'aria-label': 'search',
                    autoComplete: 'off',
                  }}
                  style={{
                    width: '100%',
                    fontFamily: this.props.regex ? 'var(--monospace-font-family)' : '',
                    fontSize: '0.8em',
                  }}
                  placeholder={this.props.placeholder}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.defaultPrevented) {
                      event.preventDefault();
                      if (event.shiftKey) {
                        this.jumpToSearchIndexWithIndexDelta(-1);
                      } else {
                        this.jumpToSearchIndexWithIndexDelta(1);
                      }
                    }
                  }}
                />
              )}
            />
          ) : (
            <StyledInputBase
              id={this.props.inputHtmlId}
              style={{
                width: '100%',
                fontFamily: this.props.regex ? 'var(--monospace-font-family)' : '',
                fontSize: '0.8em',
              }}
              placeholder={this.props.placeholder}
              inputProps={{ 'aria-label': 'search', autoComplete: 'off' }}
              value={currentValue}
              onChange={(event) => {
                const newTerm = event.target.value;
                this.debouncedTerm = newTerm;
                this.debouncedSearchCall(newTerm);
                this.forceUpdate();
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();

                  if (event.shiftKey) {
                    this.jumpToSearchIndexWithIndexDelta(-1);
                  } else {
                    this.jumpToSearchIndexWithIndexDelta(1);
                  }
                }
              }}
            />
          )}

          <StyledToggleButtonGroup>
            <Tooltip title={`Case sensitive ${this.props.caseSensitive ? '(on)' : '(off)'}`}>
              <ToggleButton
                selected={this.props.caseSensitive}
                value="casesensitive"
                onChange={() => {
                  this.props.delegate.toggleCaseSensitivity(this.props.searchId);
                }}
              >
                Aa
              </ToggleButton>
            </Tooltip>

            <Tooltip title={`Use regex ${this.props.regex ? '(on)' : '(off)'}`}>
              <ToggleButton
                selected={this.props.regex}
                value="regex"
                onChange={() => {
                  this.props.delegate.toggleRegex(this.props.searchId);
                }}
              >
                .*
              </ToggleButton>
            </Tooltip>
          </StyledToggleButtonGroup>

          {this.props.index ? (
            <>
              <Divider sx={{ height: 28, m: 0.5, marginRight: '8px' }} orientation="vertical" />

              <Stack direction="row" alignItems="center" spacing="4px">
                <div style={{ fontSize: '0.75em', userSelect: 'none' }}>
                  {`${
                    typeof this.props.index.currentIndex === 'undefined'
                      ? 0
                      : this.props.index.currentIndex + 1
                  }/${this.props.index.totalAmount}`}
                </div>

                <IconButton
                  size="small"
                  sx={{ color: 'white', width: '24px', height: '24px' }}
                  onClick={() => {
                    this.jumpToSearchIndexWithIndexDelta(1);
                  }}
                >
                  <KeyboardArrowDown />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{ color: 'white', width: '24px', height: '24px' }}
                  onClick={() => {
                    this.jumpToSearchIndexWithIndexDelta(-1);
                  }}
                >
                  <KeyboardArrowUp />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{ color: 'white', width: '24px', height: '24px', transform: 'scale(0.8)' }}
                  onClick={() => {
                    this.debouncedTerm = undefined;
                    this.props.delegate.clearSearch(this.props.searchId);
                  }}
                >
                  <Clear />
                </IconButton>
              </Stack>
            </>
          ) : null}
        </Stack>
      </Search>
    );
  }

  private async handleSuggestionClick(suggestion: SearchSuggestion) {
    this.debouncedTerm = undefined;
    this.debouncedSearchCall.cancel();
    await this.props.delegate.search(this.props.searchId, suggestion.searchTerm);
    this.props.delegate.jumpToSearchTerm(this.props.searchId, 0);
  }

  private jumpToSearchIndexWithIndexDelta(delta: number) {
    if (typeof this.props.index === 'undefined') {
      return;
    }
    if (this.props.index.totalAmount === 0) {
      return;
    }

    let nextIndex: number;
    if (typeof this.props.index.currentIndex === 'undefined') {
      if (delta < 0) {
        nextIndex = -1;
      } else {
        nextIndex = 0;
      }
    } else {
      nextIndex = this.props.index.currentIndex + delta;
    }

    if (nextIndex < 0) {
      nextIndex = this.props.index.totalAmount - 1;
    } else if (nextIndex >= this.props.index.totalAmount) {
      nextIndex = 0;
    }

    this.props.delegate.jumpToSearchTerm(this.props.searchId, nextIndex);
  }
}

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(() => ({
  '& .MuiToggleButtonGroup-grouped': {
    color: '#eee',
    width: '28px',
    height: '28px',
    fontSize: '12px',
    textTransform: 'none',
  },
  '& .Mui-selected': {
    color: 'white !important',
  },
}));

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
  },
});
